import { useEffect, useRef, useState } from "react";
import ProposalData from "../../data/global/proposal.json";

const formatRupiah = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    return "Rp " + Number(value).toLocaleString("id-ID");
};

const ProposalContainer = () => {
    const [activeSection, setActiveSection] = useState(
        ProposalData.sections[0].id
    );
    const sectionRefs = useRef({});

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: "-140px 0px -70% 0px", threshold: 0 }
        );

        ProposalData.sections.forEach(({ id }) => {
            const el = sectionRefs.current[id];
            if (el) {
                observer.observe(el);
            }
        });

        return () => observer.disconnect();
    }, []);

    const onNavClick = (e, id) => {
        e.preventDefault();
        const el = sectionRefs.current[id];
        if (el) {
            const top =
                el.getBoundingClientRect().top + window.pageYOffset - 120;
            window.scrollTo({ top, behavior: "smooth" });
        }
    };

    return (
        <div className="proposal-page">
            <div className="proposal-hero">
                <div className="container">
                    <span className="page-title">
                        {ProposalData.hero.subTitle}
                    </span>
                    <h1 className="title">{ProposalData.hero.title}</h1>
                    <p className="tagline">{ProposalData.hero.tagline}</p>
                    <div className="meta">
                        <span>
                            <i className="icon_calendar"></i>{" "}
                            {ProposalData.hero.date}
                        </span>
                        <span>
                            <i className="icon_pin_alt"></i>{" "}
                            {ProposalData.hero.preparedBy}
                        </span>
                    </div>
                </div>
            </div>

            <div className="container proposal-body">
                <div className="row">
                    <div className="col-lg-3">
                        <nav className="proposal-nav">
                            <span className="proposal-nav-title">
                                Daftar Isi
                            </span>
                            <ul>
                                {ProposalData.sections.map((section) => (
                                    <li key={section.id}>
                                        <a
                                            href={`#${section.id}`}
                                            className={
                                                activeSection === section.id
                                                    ? "active"
                                                    : ""
                                            }
                                            onClick={(e) =>
                                                onNavClick(e, section.id)
                                            }
                                        >
                                            {section.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>

                    <div className="col-lg-9">
                        <section
                            id="pendahuluan"
                            ref={(el) =>
                                (sectionRefs.current["pendahuluan"] = el)
                            }
                            className="proposal-section"
                        >
                            <h2 className="section-heading">Pendahuluan</h2>

                            <h3 className="sub-heading">Latar Belakang</h3>
                            {ProposalData.pendahuluan.latarBelakang.map(
                                (p, i) => (
                                    <p key={i}>{p}</p>
                                )
                            )}

                            <h3 className="sub-heading">Dasar Pemikiran</h3>
                            <ul className="proposal-list">
                                {ProposalData.pendahuluan.dasarPemikiran.map(
                                    (item, i) => (
                                        <li key={i}>{item}</li>
                                    )
                                )}
                            </ul>

                            <h3 className="sub-heading">Nama Kegiatan</h3>
                            <p>{ProposalData.pendahuluan.namaKegiatan}</p>

                            <h3 className="sub-heading">Manfaat</h3>
                            <ul className="proposal-list">
                                {ProposalData.pendahuluan.manfaat.map(
                                    (item, i) => (
                                        <li key={i}>{item}</li>
                                    )
                                )}
                            </ul>
                        </section>

                        <section
                            id="pelaksanaan"
                            ref={(el) =>
                                (sectionRefs.current["pelaksanaan"] = el)
                            }
                            className="proposal-section"
                        >
                            <h2 className="section-heading">
                                Rencana Pelaksanaan
                            </h2>
                            <p>{ProposalData.pelaksanaan.desc}</p>
                            <div className="proposal-info-grid">
                                {ProposalData.pelaksanaan.items.map(
                                    (item, i) => (
                                        <div
                                            className="proposal-info-item"
                                            key={i}
                                        >
                                            <span className="label">
                                                {item.label}
                                            </span>
                                            <span className="value">
                                                {item.value}
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>
                        </section>

                        <section
                            id="anggaran"
                            ref={(el) =>
                                (sectionRefs.current["anggaran"] = el)
                            }
                            className="proposal-section"
                        >
                            <h2 className="section-heading">
                                Rencana Anggaran Biaya
                            </h2>
                            <p>{ProposalData.anggaran.note}</p>

                            <h3 className="sub-heading">A. Pemasukan</h3>
                            <div className="table-responsive">
                                <table className="table proposal-table">
                                    <thead>
                                        <tr>
                                            <th>No</th>
                                            <th>Komponen</th>
                                            <th>Vol</th>
                                            <th>Sat</th>
                                            <th>Harga (Rp)</th>
                                            <th>Jumlah (Rp)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ProposalData.anggaran.pemasukan.rows.map(
                                            (row) => (
                                                <tr key={row.no}>
                                                    <td>{row.no}</td>
                                                    <td>{row.komponen}</td>
                                                    <td>{row.vol}</td>
                                                    <td>{row.sat}</td>
                                                    <td>
                                                        {row.harga
                                                            ? formatRupiah(
                                                                  row.harga
                                                              )
                                                            : "-"}
                                                    </td>
                                                    <td>
                                                        {row.jumlah
                                                            ? formatRupiah(
                                                                  row.jumlah
                                                              )
                                                            : "-"}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="5">Total</td>
                                            <td>
                                                {formatRupiah(
                                                    ProposalData.anggaran
                                                        .pemasukan.total
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <h3 className="sub-heading">B. Pengeluaran</h3>
                            <div className="table-responsive">
                                <table className="table proposal-table">
                                    <thead>
                                        <tr>
                                            <th>No</th>
                                            <th>Komponen</th>
                                            <th>Vol</th>
                                            <th>Sat</th>
                                            <th>Harga (Rp)</th>
                                            <th>Jumlah (Rp)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ProposalData.anggaran.pengeluaran.rows.map(
                                            (row) => (
                                                <tr key={row.no}>
                                                    <td>{row.no}</td>
                                                    <td>{row.komponen}</td>
                                                    <td>{row.vol}</td>
                                                    <td>{row.sat}</td>
                                                    <td>
                                                        {formatRupiah(
                                                            row.harga
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatRupiah(
                                                            row.jumlah
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="5">Total</td>
                                            <td>
                                                {formatRupiah(
                                                    ProposalData.anggaran
                                                        .pengeluaran.total
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <h3 className="sub-heading">C. Detail Lainnya</h3>
                            <div className="table-responsive">
                                <table className="table proposal-table">
                                    <tbody>
                                        {ProposalData.anggaran.detailLainnya.map(
                                            (group, gi) =>
                                                group.rincian.map(
                                                    (item, ri) => (
                                                        <tr key={`${gi}-${ri}`}>
                                                            {ri === 0 && (
                                                                <td
                                                                    rowSpan={
                                                                        group
                                                                            .rincian
                                                                            .length
                                                                    }
                                                                    className="category-cell"
                                                                >
                                                                    {
                                                                        group.kategori
                                                                    }
                                                                </td>
                                                            )}
                                                            <td>
                                                                {item.label}
                                                            </td>
                                                            <td>
                                                                {
                                                                    item.keterangan
                                                                }
                                                            </td>
                                                            <td>
                                                                {formatRupiah(
                                                                    item.jumlah
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )
                                                )
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="proposal-summary">
                                {ProposalData.anggaran.ringkasan.map(
                                    (item, i) => (
                                        <div
                                            className="proposal-summary-item"
                                            key={i}
                                        >
                                            <span className="label">
                                                {item.label}
                                            </span>
                                            <span className="value">
                                                {formatRupiah(item.value)}
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>
                        </section>

                        <section
                            id="ketentuan"
                            ref={(el) =>
                                (sectionRefs.current["ketentuan"] = el)
                            }
                            className="proposal-section"
                        >
                            <h2 className="section-heading">
                                Ketentuan Peserta &amp; Pertandingan
                            </h2>

                            <h3 className="sub-heading">Ketentuan Peserta</h3>
                            <ol className="proposal-list proposal-list-numbered">
                                {ProposalData.ketentuan.peserta.map(
                                    (item, i) => (
                                        <li key={i}>{item}</li>
                                    )
                                )}
                            </ol>

                            <h3 className="sub-heading">
                                Sistem Pertandingan
                            </h3>
                            <p>{ProposalData.ketentuan.sistemPertandingan}</p>

                            <h3 className="sub-heading">Penghargaan</h3>
                            <ul className="proposal-list">
                                {ProposalData.ketentuan.penghargaan.map(
                                    (item, i) => (
                                        <li key={i}>{item}</li>
                                    )
                                )}
                            </ul>

                            <p className="proposal-note">
                                {ProposalData.ketentuan.penutupNote}
                            </p>
                        </section>

                        <section
                            id="sumber-dana"
                            ref={(el) =>
                                (sectionRefs.current["sumber-dana"] = el)
                            }
                            className="proposal-section"
                        >
                            <h2 className="section-heading">
                                Sumber Dana Kegiatan
                            </h2>
                            <ul className="proposal-list">
                                {ProposalData.sumberDana.sumber.map(
                                    (item, i) => (
                                        <li key={i}>{item}</li>
                                    )
                                )}
                            </ul>

                            <div className="proposal-bank-card">
                                <div className="proposal-bank-item">
                                    <span className="label">
                                        Registrasi, Donasi &amp; Sponsorship
                                    </span>
                                    <span className="value">
                                        {ProposalData.sumberDana.rekening.bank}
                                        <br />
                                        a.n{" "}
                                        {
                                            ProposalData.sumberDana.rekening
                                                .atasNama
                                        }
                                        <br />
                                        <strong>
                                            {
                                                ProposalData.sumberDana
                                                    .rekening.nomor
                                            }
                                        </strong>
                                    </span>
                                </div>
                            </div>
                        </section>

                        <section
                            id="panitia"
                            ref={(el) => (sectionRefs.current["panitia"] = el)}
                            className="proposal-section"
                        >
                            <h2 className="section-heading">
                                Susunan Panitia
                            </h2>
                            <div className="proposal-panitia-grid">
                                {ProposalData.panitia.map((person, i) => (
                                    <div
                                        className="proposal-panitia-item"
                                        key={i}
                                    >
                                        <div className="avatar">
                                            <i className="icon_profile"></i>
                                        </div>
                                        <span className="role">
                                            {person.role}
                                        </span>
                                        <span className="name">
                                            {person.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section
                            id="penutup"
                            ref={(el) => (sectionRefs.current["penutup"] = el)}
                            className="proposal-section"
                        >
                            <h2 className="section-heading">Penutup</h2>
                            <p>{ProposalData.penutup.body}</p>
                            <p className="proposal-closing-line">
                                {ProposalData.penutup.closingLine}
                            </p>

                            <div className="proposal-signature">
                                <span>{ProposalData.penutup.place}</span>
                                <span className="role">Ketua Pelaksana</span>
                                <span className="name">
                                    {ProposalData.penutup.ketuaPelaksana}
                                </span>
                            </div>

                            <div className="proposal-contact-list">
                                <div className="proposal-contact-item">
                                    <i className="icofont-instagram"></i>
                                    <span>
                                        IG {ProposalData.penutup.contact.instagram}
                                    </span>
                                </div>
                                <div className="proposal-contact-item">
                                    <i className="icon_phone"></i>
                                    <span>
                                        {ProposalData.penutup.contact.phone}
                                    </span>
                                </div>
                                <div className="proposal-contact-item">
                                    <i className="icon_mail_alt"></i>
                                    <a
                                        href={`mailto:${ProposalData.penutup.contact.email}`}
                                    >
                                        {ProposalData.penutup.contact.email}
                                    </a>
                                </div>
                                <div className="proposal-contact-item">
                                    <i className="icon_globe"></i>
                                    <a
                                        href={`https://${ProposalData.penutup.contact.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {ProposalData.penutup.contact.website}
                                    </a>
                                </div>
                                <div className="proposal-contact-item">
                                    <i className="icon_pin_alt"></i>
                                    <span>
                                        {
                                            ProposalData.penutup.contact
                                                .address
                                        }
                                    </span>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProposalContainer;
